// Plan-EnterpriseUpsellViaWidget.md §A.2 (2026-05-13)
//
// Helpers for the customer dashboard to drive the embedded Prodagi Help
// Widget iframe. The widget is mounted by `ProdagiHelpWidget` (which
// injects `public/widget.js`); widget.js gives the iframe the stable id
// `#prodagi-widget-iframe` so the parent can postMessage commands.
//
// Commands:
//   - `openEnterpriseUpgradeChat()` — the Enterprise CTA on BillingPage +
//     Pricing. Falls back to the historical mailto link when the widget
//     isn't mounted (e.g. dev env without `VITE_PRODAGI_GUIDE_AGENT_ID`,
//     or a network race before widget.js has injected the iframe). The
//     Enterprise CTA is never a dead-end.
//   - `openHelpChat(prompt?)` — generic "talk to us" opener for any
//     in-app support CTA. Reports whether the widget was actually
//     available so the caller can fall back to its own copy.

import { ENTERPRISE_MAILTO } from '../types/billing';

/** Auto-injected text the admin agent will see as the first user message
 *  in the Enterprise upgrade chat. The agent picks up from here per its
 *  prompt — confirms identity, asks 1-2 qualifying questions, files a
 *  ticket with `kind: enterprise_upgrade_request`. */
const ENTERPRISE_UPGRADE_PROMPT =
  "I'd like to upgrade my workspace to the Enterprise plan.";

/** Find the Prodagi Help Widget iframe injected by `public/widget.js`. */
function getWidgetIframe(): HTMLIFrameElement | null {
  return document.querySelector<HTMLIFrameElement>('#prodagi-widget-iframe');
}

/**
 * Open the Prodagi Help Widget on the customer dashboard with the
 * Enterprise upgrade prompt pre-sent. Falls back to mailto when the
 * widget isn't available (env unset / launcher not yet mounted).
 *
 * Called from `BillingPage.tsx` and `Pricing.tsx` Enterprise CTAs.
 * Internal workspaces short-circuit to "Managed by admin" before
 * reaching this util — no special handling needed here.
 */
export function openEnterpriseUpgradeChat(): void {
  const iframe = getWidgetIframe();
  if (!iframe?.contentWindow) {
    // Widget not mounted (no VITE_PRODAGI_GUIDE_AGENT_ID configured, or
    // widget.js hasn't injected yet). Don't strand the user — fall back
    // to the historical mailto so the CTA still works.
    window.location.href = ENTERPRISE_MAILTO;
    return;
  }
  iframe.contentWindow.postMessage(
    {
      type: 'prodagi_widget_send_message',
      text: ENTERPRISE_UPGRADE_PROMPT,
      open: true,
    },
    window.location.origin,
  );
}

/**
 * Generic "contact support" opener: raise the Prodagi Help Widget panel and,
 * optionally, pre-send `prompt` as the first user message so the support
 * agent starts with context.
 *
 * Unlike `openEnterpriseUpgradeChat` this also *shows* the panel — the
 * postMessage command alone only talks to the iframe, while the wrapper's
 * visibility is owned by widget.js (`window.ProdagiWidget.open()` /
 * the `prodagi:open-widget` DOM event).
 *
 * @returns `true` when the widget was available and the command was sent,
 *          `false` when it isn't mounted — callers should then surface their
 *          own fallback (e.g. an email address) rather than a dead button.
 */
export function openHelpChat(prompt?: string): boolean {
  const iframe = getWidgetIframe();
  if (!iframe?.contentWindow) return false;

  const api = (window as unknown as { ProdagiWidget?: { open?: () => void } })
    .ProdagiWidget;
  if (typeof api?.open === 'function') {
    api.open();
  } else {
    // widget.js also listens on this document event (same handler).
    document.dispatchEvent(new Event('prodagi:open-widget'));
  }

  const text = prompt?.trim();
  if (text) {
    iframe.contentWindow.postMessage(
      { type: 'prodagi_widget_send_message', text, open: true },
      window.location.origin,
    );
  }
  return true;
}
